import { useState } from 'react';
import { X, Clock } from 'lucide-react';
import { useSlaPolicy, type Priority, type SlaPolicy } from '@/lib/sla';
import { cn } from '@/lib/utils';

const PRIORITIES: Priority[] = ['P1', 'P2', 'P3', 'P4'];

export function SlaPolicyEditor({
  open,
  teamId,
  teamName,
  onClose,
}: {
  open: boolean;
  teamId: string;
  teamName: string;
  onClose: () => void;
}) {
  const { policy, save } = useSlaPolicy(teamId);
  const [draft, setDraft] = useState<SlaPolicy>(policy);

  if (!open) return null;

  const setTarget = (p: Priority, key: 'responseH' | 'resolutionH', v: number) =>
    setDraft((d) => ({ ...d, targets: { ...d.targets, [p]: { ...d.targets[p], [key]: v } } }));

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-6 pt-[10vh]">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <div className="flex items-center gap-2 text-base font-semibold"><Clock className="h-4 w-4 text-primary" /> SLA policy</div>
            <div className="text-xs text-muted-foreground">Response & resolution targets for {teamName}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-5">
          {/* calendar */}
          <div className="mb-4 flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Measured against:</span>
            <div className="flex rounded-lg border border-border p-0.5 text-xs">
              {(['24x7', 'business'] as const).map((c) => (
                <button
                  key={c}
                  onClick={() => setDraft((d) => ({ ...d, calendar: c }))}
                  className={cn('rounded-md px-2.5 py-1 font-medium transition', draft.calendar === c ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}
                >
                  {c === '24x7' ? '24 / 7' : 'Business hrs (Mon–Fri 9–5)'}
                </button>
              ))}
            </div>
          </div>

          {/* targets table */}
          <div className="overflow-hidden rounded-xl border border-border">
            <div className="grid grid-cols-3 border-b border-border bg-muted/50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span>Priority</span>
              <span>Response (h)</span>
              <span>Resolution (h)</span>
            </div>
            {PRIORITIES.map((p) => (
              <div key={p} className="grid grid-cols-3 items-center border-b border-border/60 px-3 py-2 last:border-0">
                <span className="font-mono text-sm font-medium">{p}</span>
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  value={draft.targets[p].responseH}
                  onChange={(e) => setTarget(p, 'responseH', Number(e.target.value))}
                  className="h-8 w-24 rounded-md border border-input bg-background px-2 text-sm outline-none focus:border-primary"
                />
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={draft.targets[p].resolutionH}
                  onChange={(e) => setTarget(p, 'resolutionH', Number(e.target.value))}
                  className="h-8 w-24 rounded-md border border-input bg-background px-2 text-sm outline-none focus:border-primary"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <button onClick={onClose} className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium transition hover:bg-muted">Cancel</button>
          <button
            onClick={() => { save(draft); onClose(); }}
            className="rounded-lg bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground transition hover:opacity-90"
          >
            Save policy
          </button>
        </div>
      </div>
    </div>
  );
}
